import * as React from 'react';
import { ChevronDown } from 'lucide-react';
import {
  statusControlInnerRecipe,
  statusControlRecipe,
} from '../recipes/mobile.recipe';
import { Badge } from './Badge';
import { StatusDot } from './Chip';
import { Icon } from './Icon';
import { Spinner } from './Spinner';
import { Inline } from './Layout';
import { OptionSheet } from './OptionSheet';
import { PickerMenu, PickerMenuOption, type PickerMenuProps } from './Picker';
import { StatusChip } from './StatusChip';
import type { StatusTone } from './status-tone';
import { Text } from './Text';

export interface StatusPickerStatus {
  value: string;
  label: string;
  /** The badge colour for this status. Neutral when left off. */
  tone?: StatusTone;
  /**
   * Shown but not choosable: a status this call cannot take from here, such
   * as Completed on an Urgently call. The screen decides which; the picker
   * only draws it.
   */
  disabled?: boolean;
}

export interface StatusPickerProps
  extends Omit<PickerMenuProps, 'trigger' | 'children' | 'prefix'> {
  /** The statuses that can be chosen, in the order they happen. */
  statuses: StatusPickerStatus[];
  /** The current status. Need not be one of `statuses` — see `valueLabel`. */
  value: string;
  /**
   * How to show a value that is not in the list — a terminal status the
   * picker cannot set, like GOA. Falls back to the raw value.
   */
  valueLabel?: string;
  valueTone?: StatusTone;
  onValueChange: (value: string) => void;
  /** Words before the badge on the closed control. */
  prefix?: React.ReactNode;
  /**
   * A change is saving: the options grey out and the trigger will not open,
   * but the menu stays where it is so the screen can close it when the save
   * settles.
   */
  busy?: boolean;
  /**
   * `menu` drops a list from the control, for a desk. `sheet` is the phone's
   * form: the control is the Figma's Status Control, a white pill in a wash
   * with the status's dot, word and a caret, and the statuses rise as
   * status chips in an OptionSheet, with Done at its foot and no close
   * button, as the Figma's Call Status sheet (344:64824) draws it.
   */
  presentation?: 'menu' | 'sheet';
  /** The sheet's heading. Sheet form only. */
  title?: string;
  /**
   * The control spans its container: the foot of a call, in its sheet over
   * the map or along the bottom of its page. Left off, it hugs its content.
   * Sheet form only.
   */
  fullWidth?: boolean;
}

/**
 * A PickerMenu over a list of statuses, each drawn as a dotted Badge in its
 * own tone — the call's status on the profile page.
 *
 *   <StatusPicker
 *     statuses={STATUSES}
 *     value={call.status}
 *     onValueChange={change}
 *     open={open}
 *     onOpenChange={setOpen}
 *     busy={isSaving}
 *   />
 *
 * Like ReasonPicker, the screen owns the open state: a choice may need a
 * request, or a follow-up dialog, before the menu should close.
 *
 * `presentation="sheet"` is the call footer's status pill on a phone. Mark
 * a status the call cannot take `disabled` and it shows without being
 * choosable.
 */
export const StatusPicker = React.forwardRef<HTMLDivElement, StatusPickerProps>(
  function StatusPicker(
    {
      statuses,
      value,
      valueLabel,
      valueTone,
      onValueChange,
      prefix = 'Status:',
      busy = false,
      disabled = false,
      onOpenChange,
      presentation = 'menu',
      title = 'Status',
      fullWidth = false,
      ...props
    },
    ref
  ) {
    const current = statuses.find((status) => status.value === value);
    const label = current?.label ?? valueLabel ?? value;
    const tone = current?.tone ?? valueTone ?? 'neutral';

    if (presentation === 'sheet') {
      return (
        <OptionSheet
          title={title}
          open={props.open}
          onOpenChange={(open) => {
            if (!disabled && !busy) onOpenChange(open);
          }}
          value={value}
          onValueChange={onValueChange}
          doneLabel="Done"
          layout="chips"
          options={statuses.map((status) => ({
            value: status.value,
            disabled: busy || status.disabled,
            label: (
              <StatusChip label={status.label} tone={status.tone} size="lg" />
            ),
          }))}
          trigger={
            <button
              type="button"
              disabled={disabled || busy}
              aria-busy={busy || undefined}
              className={statusControlRecipe({ fullWidth })}
            >
              <span className={statusControlInnerRecipe()}>
                <Inline as="span" gap={2} align="center">
                  {busy ? (
                    <Spinner size="sm" />
                  ) : (
                    <StatusDot tone={tone} size="md" />
                  )}
                  {prefix ? <span className="sr-only">{prefix} </span> : null}
                  <Text as="span" variant="body-lg-strong">
                    {label}
                  </Text>
                </Inline>
                <Icon icon={ChevronDown} size="sm" />
              </span>
            </button>
          }
        />
      );
    }

    return (
      <PickerMenu
        ref={ref}
        width="sm"
        disabled={disabled || busy}
        onOpenChange={(open) => {
          if (!disabled && !busy) onOpenChange(open);
        }}
        trigger={
          <Inline gap={2} align="center">
            {prefix ? (
              <Text variant="body-sm" as="span" tone="muted">
                {prefix}
              </Text>
            ) : null}
            <Badge tone={tone}>{label}</Badge>
          </Inline>
        }
        {...props}
      >
        {statuses.map((status) => (
          <PickerMenuOption
            key={status.value}
            onClick={() => onValueChange(status.value)}
            disabled={busy || status.disabled}
            selected={value === status.value}
          >
            <Badge tone={status.tone ?? 'neutral'}>{status.label}</Badge>
          </PickerMenuOption>
        ))}
      </PickerMenu>
    );
  }
);
